import { useState, useCallback, useMemo } from 'react';
import userGuidesData from 'data/userGuides.json';

/**
 * Custom hook for managing user guides
 * Provides easy access to guide data and search functionality
 */
export const useUserGuides = () => {
  const [currentGuideId, setCurrentGuideId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Get all guides
  const allGuides = useMemo(() => Object.values(userGuidesData.guides), []);

  // Get all categories
  const categories = useMemo(() => userGuidesData.categories, []);

  // Get current guide
  const currentGuide = useMemo(() => {
    return currentGuideId ? userGuidesData.guides[currentGuideId] : null;
  }, [currentGuideId]);

  // Search guides function
  const searchGuides = useCallback((query, category = 'all') => {
    const searchTerm = query.toLowerCase().trim();
    
    if (!searchTerm && category === 'all') {
      return allGuides;
    }

    return allGuides.filter(guide => {
      // Category filter
      if (category !== 'all' && guide.category !== category) {
        return false;
      }

      // Text search in multiple fields
      const searchFields = [
        guide.title,
        guide.description,
        ...guide.tags,
        ...guide.steps.map(step => step.title),
        ...guide.steps.map(step => step.description),
        ...guide.steps.flatMap(step => step.actions || []),
        ...(guide.common_errors?.map(error => error.error) || []),
        ...(guide.common_errors?.map(error => error.solution) || [])
      ];

      return searchFields.some(field => 
        field && field.toLowerCase().includes(searchTerm)
      );
    });
  }, [allGuides]);

  // Get guide by ID
  const getGuideById = useCallback((guideId) => {
    return userGuidesData.guides[guideId] || null;
  }, []);

  // Get guides by tag
  const getGuidesByTag = useCallback((tag) => {
    return allGuides.filter(guide =>
      guide.tags.some(t => t.toLowerCase().includes(tag.toLowerCase()))
    );
  }, [allGuides]);

  // Get guides by category
  const getGuidesByCategory = useCallback((category) => {
    return allGuides.filter(guide => guide.category === category);
  }, [allGuides]);

  // Get related guides
  const getRelatedGuides = useCallback((guideId, limit = 3) => {
    const guide = getGuideById(guideId);
    if (!guide) return [];

    // Get guides from same category first
    const sameCategory = getGuidesByCategory(guide.category)
      .filter(g => g.id !== guideId);

    // Get guides with similar tags
    const relatedByTags = allGuides.filter(g => {
      if (g.id === guideId) return false;
      return g.tags.some(tag => guide.tags.includes(tag));
    });

    // Combine and deduplicate
    const related = [...sameCategory, ...relatedByTags]
      .filter((guide, index, self) => 
        index === self.findIndex(g => g.id === guide.id)
      );

    return related.slice(0, limit);
  }, [allGuides, getGuideById, getGuidesByCategory]);

  // Navigation functions
  const startGuide = useCallback((guideId) => {
    setCurrentGuideId(guideId);
  }, []);

  const closeGuide = useCallback(() => {
    setCurrentGuideId(null);
  }, []);

  // Search with query state management
  const performSearch = useCallback((query, category = 'all') => {
    setSearchQuery(query);
    return searchGuides(query, category);
  }, [searchGuides]);

  // AI-friendly functions (for external access)
  const aiHelpers = useMemo(() => ({
    findGuideForTask: (task) => {
      const taskKeywords = {
        'osto': ['purchase_transaction'],
        'myynti': ['selling_transaction'],
        'osinko': ['dividend_transaction'],
        'salkku': ['portfolio_management'],
        'raportti': ['reports'],
        'kirjaus': ['purchase_transaction', 'selling_transaction', 'dividend_transaction']
      };

      const taskLower = task.toLowerCase();
      for (const [keyword, guideIds] of Object.entries(taskKeywords)) {
        if (taskLower.includes(keyword)) {
          return guideIds.map(id => getGuideById(id)).filter(Boolean);
        }
      }

      // Fallback to search
      return searchGuides(task);
    },

    suggestGuideForError: (errorMessage) => {
      const errorLower = errorMessage.toLowerCase();
      
      // Find guides that mention this error
      return allGuides.filter(guide => {
        if (!guide.common_errors) return false;
        return guide.common_errors.some(error => 
          errorLower.includes(error.error.toLowerCase()) ||
          error.error.toLowerCase().includes(errorLower)
        );
      });
    },

    getQuickStartGuides: () => {
      return allGuides.filter(guide => 
        guide.difficulty === 'beginner' && 
        guide.tags.includes('aloitus')
      );
    }
  }), [allGuides, getGuideById, searchGuides]);

  return {
    // Data
    allGuides,
    categories,
    currentGuide,
    currentGuideId,
    searchQuery,

    // Search functions
    searchGuides,
    performSearch,
    getGuideById,
    getGuidesByTag,
    getGuidesByCategory,
    getRelatedGuides,

    // Navigation
    startGuide,
    closeGuide,
    setCurrentGuideId,

    // AI helpers
    aiHelpers,

    // Utilities
    metadata: userGuidesData.metadata
  };
};

export default useUserGuides;
